import { useQuery } from "@tanstack/react-query";
import { Effect } from "effect";
import { createContext, memo, ReactNode, useContext } from "react";
import {
  EffectApiWidgetClientContext,
  type EffectApiWidgetContext,
} from "./effect-api-client";

export const SupportV2Context = createContext<boolean | undefined>(undefined);

const SUPPORT_V2_QUERY_KEY = ["widgetCapabilities", "supportV2"] as const;

export function shouldUseSupportV2Api(
  supportV2: boolean | undefined,
  effectApi: EffectApiWidgetContext | undefined,
): effectApi is EffectApiWidgetContext {
  return supportV2 !== false && effectApi !== undefined;
}

export const SupportV2Provider = memo(function SupportV2Provider({
  children,
}: {
  children: ReactNode;
}) {
  const effectApi = useContext(EffectApiWidgetClientContext);

  const { data } = useQuery({
    queryKey: SUPPORT_V2_QUERY_KEY,
    queryFn: async (): Promise<{ supportV2: boolean }> => {
      if (!effectApi) {
        return { supportV2: false };
      }
      const client = effectApi.getClient();
      return await effectApi.runPromise(
        client.chatWidget
          .getCapabilities()
          .pipe(Effect.catchAll(() => Effect.succeed({ supportV2: false }))),
      );
    },
    enabled: !!effectApi,
    staleTime: 1000 * 60 * 30, // 30 minutes — capability rarely changes
    gcTime: 1000 * 60 * 60, // 1 hour
  });

  return (
    <SupportV2Context.Provider value={data?.supportV2}>
      {children}
    </SupportV2Context.Provider>
  );
});
